<template>
  <div class="gsl-hrm-attachment">
    <oxd-divider />
    <save-project-allocation
      v-if="showSaveModal"
      :http="http"
      @close="onSaveModalClose"
    >
    </save-project-allocation>
    <edit-project-allocation
      v-else-if="showEditModal"
      :data="editModalState"
      :http="http"
      @close="onEditModalClose"
    ></edit-project-allocation>
    <template v-else>
      <div class="gsl-hrm-horizontal-padding gsl-hrm-vertical-padding">
        <profile-action-header
          :action-button-shown="$can.create(`${screen}_project_allocation`)"
          @click="onClickAdd"
        >
          {{ $t('general.project_allocation') }}
        </profile-action-header>
      </div>
      <table-header
        :selected="checkedItems.length"
        :total="total"
        :loading="isLoading"
        @delete="onClickDeleteSelected"
      ></table-header>
      <div class="gsl-hrm-container">
        <oxd-card-table
          v-model:selected="checkedItems"
          :headers="tableHeaders"
          :items="items?.data"
          :selectable="$can.delete(`${screen}_project_allocation`)"
          :clickable="false"
          :loading="isLoading"
          class="gsl-hrm-allocation-list"
          row-decorator="oxd-table-decorator-card"
        />
      </div>
      <div v-if="showPaginator" class="gsl-hrm-bottom-container">
        <oxd-pagination v-model:current="currentPage" :length="pages" />
      </div>
    </template>
    <delete-confirmation
      ref="deleteDialog"
      message="The selected Project Allocation will be permanently deleted. Are you sure you want to continue?"
    ></delete-confirmation>
  </div>
</template>
<script>
import {h} from 'vue';
import {APIService} from '@/core/util/services/api.service';
import usePaginate from '@ohrm/core/util/composable/usePaginate';
import SaveProjectAllocation from '@/orangehrmPimPlugin/components/SaveProjectAllocation.vue';
import EditProjectAllocation from '@/orangehrmPimPlugin/components/EditProjectAllocation.vue';
import ProfileActionHeader from '@/orangehrmPimPlugin/components/ProfileActionHeader';
import DeleteConfirmationDialog from '@ohrm/components/dialogs/DeleteConfirmationDialog.vue';
import StatusCell from '@ohrm/core/components/table/StatusCell';

export default {
  name: 'ProfileProjectAllocation',
  components: {
    'save-project-allocation': SaveProjectAllocation,
    'edit-project-allocation': EditProjectAllocation,
    'profile-action-header': ProfileActionHeader,
    'delete-confirmation': DeleteConfirmationDialog,
  },
  props: {
    employeeId: {
      type: String,
      required: true,
    },
    screen: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const http = new APIService(
      window.appGlobal.baseUrl,
      `/api/v2/pim/employees/${props.employeeId}/screen/${props.screen}/project-allocation`,
    );

    const projectAllocationDataNormalizer = (data) => {
      return data.map((item) => {
        const endDate = new Date(item.endDate);
        const currentDate = new Date();

        // Determine the status based on the endDate
        const status = endDate < currentDate ? 'Inactive' : 'Active';

        return {
          ...item,
          projectName:
            item.project.customerName + ' - ' + item.project.projectName,
          status: status,
          role: item?.role?.name || '-',
          percentage: item.percentage + '%',
        };
      });
    };
    const {
      showPaginator,
      currentPage,
      total,
      pages,
      pageSize,
      response,
      isLoading,
      execQuery,
    } = usePaginate(http, {
      normalizer: projectAllocationDataNormalizer,
      toastNoRecords: false,
    });

    return {
      http,
      showPaginator,
      currentPage,
      isLoading,
      total,
      pages,
      pageSize,
      execQuery,
      items: response,
    };
  },
  data() {
    return {
      headers: [
        {
          name: 'projectName',
          slot: 'title',
          title: this.$t('time.project_name'),
          style: {flex: 1},
        },

        {
          name: 'startDate',
          title: this.$t('general.start_date'),
          style: {flex: 1},
        },
        {
          name: 'endDate',
          title: this.$t('general.end_date'),
          style: {flex: 1},
        },
        {
          name: 'status',
          title: this.$t('general.status'),
          style: {flex: 1},
          slot: 'action',
          cellRenderer: this.cellRenderer,
        },
        {
          name: 'role',
          title: this.$t('general.role'),
          style: {flex: 1},
        },

        {
          name: 'percentage',
          title: this.$t('general.percentage_of_allocation'),
          style: {flex: 1},
        },
      ],
      checkedItems: [],
      showSaveModal: false,
      showEditModal: false,
      editModalState: null,
    };
  },
  computed: {
    tableHeaders() {
      const headerActions = {
        name: 'actions',
        slot: 'footer',
        title: this.$t('general.actions'),
        style: {flex: 1},
        cellType: 'oxd-table-cell-actions',
        cellConfig: {},
      };
      if (this.$can.update(`${this.screen}_project_allocation`)) {
        headerActions.cellConfig.edit = {
          onClick: this.onClickEdit,
          props: {
            name: 'pencil-fill',
          },
        };
      }
      if (this.$can.delete(`${this.screen}_project_allocation`)) {
        headerActions.cellConfig.delete = {
          onClick: this.onClickDelete,
          component: 'oxd-icon-button',
          props: {
            name: 'trash',
          },
        };
      }

      return Object.keys(headerActions.cellConfig).length > 0
        ? this.headers.concat([headerActions])
        : this.headers;
    },
  },
  methods: {
    onClickDeleteSelected() {
      const ids = this.checkedItems.map((index) => {
        return this.items?.data[index].id;
      });
      this.$refs.deleteDialog.showDialog().then((confirmation) => {
        if (confirmation === 'ok') {
          this.deleteItems(ids);
        }
      });
    },
    onClickDelete(item) {
      this.$refs.deleteDialog.showDialog().then((confirmation) => {
        if (confirmation === 'ok') {
          this.deleteItems([item.id]);
        }
      });
    },
    deleteItems(items) {
      if (items instanceof Array) {
        this.isLoading = true;
        this.http
          .deleteAll({
            ids: items,
          })
          .then(() => {
            return this.$toast.deleteSuccess();
          })
          .then(() => {
            this.isLoading = false;
            this.resetDataTable();
          });
      }
    },
    async resetDataTable() {
      this.checkedItems = [];
      await this.execQuery();
    },
    onClickAdd() {
      this.showEditModal = false;
      this.editModalState = null;
      this.showSaveModal = true;
    },
    onClickEdit(item) {
      this.showSaveModal = false;
      this.editModalState = item;
      this.showEditModal = true;
    },

    onSaveModalClose() {
      this.showSaveModal = false;
      this.resetDataTable();
    },
    onEditModalClose() {
      this.showEditModal = false;
      this.editModalState = null;
      this.resetDataTable();
    },
    cellRenderer(...args) {
      const cellData = args[1];
      return {
        component: StatusCell,
        props: {
          data: cellData,
        },
      };
    },
  },
};
</script>
<style lang="scss" scoped>
.gsl-hrm-allocation-list {
  ::v-deep(.card-header-slot) {
    border-bottom: $oxd-input-control-border--active;
    margin-bottom: 5px;
  }

  ::v-deep(.card-body-slot) {
    display: unset;
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-gap: 0.2rem;
  }
  ::v-deep(.card-header-slot-content.--right) {
    justify-items: center;
    align-items: center;
    display: flex;
  }
  ::v-deep(.card-footer-slot) {
    display: flex;
    align-items: flex-end;
  }
}
</style>
